import * as yup from '@if/core/yup'
import { EFinancingCompany, getEFinancingCompanyList } from '@if/core/types'
import { PageContainer, PageTabs } from '@if/mui'
import { useGetCustomer } from '@src/api/customer-api'
import { useGetCalendarSimulation } from '@src/api/loan/loan-api'
import LoanCalendarActions from '@src/components/LoanCalendar/LoanCalendarActions'
import LoanCalendarContent from '@src/components/LoanCalendar/LoanCalendarContent'
import { ELoanStatusSet, toLoanStatusValue } from '@src/components/LoanCalendar/loan-status'
import LoanCalendarSimulationProvider from '@src/components/LoanCalendar/LoanCalendarSimulationProvider'
import LoanTransactionList from '@src/components/LoanTransaction/LoanTransactionList'
import LoanLockProvider from '@src/providers/LoanLockProvider'
import { useCompany } from '@src/services/company-hooks'
import { isProd } from '@src/types/constants'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

const CALENDAR_PARAMS = ['company', 'id'] as const

const CALENDAR_TAB = 'calendar'
const TRANSACTION_TAB = 'transaction'

const calendarParamsSchema = yup.object({
  company: yup.mixed<EFinancingCompany>().required().oneOf(getEFinancingCompanyList()),
  id: yup.text().required(),
})

type CalendarParams = yup.InferSubmit<typeof calendarParamsSchema>

// A loan of another company than the URL's answers 404, which the page renders as not found.
const useCalendarSimulationResource = ({ company, id }: CalendarParams) => ({
  signedSimulation: useGetCalendarSimulation(id, company),
})

const useCustomerResource = ({ signedSimulation }: { signedSimulation: SignedCalendarSimulation }) => ({
  customer: useGetCustomer(
    signedSimulation.simulation.fullLoan.loan.borrowerId,
    signedSimulation.simulation.fullLoan.loan.financingCompanyId,
  ),
})

const LoanCalendarPage = () => {
  const { t } = useTranslation()
  const { tab } = useParams()
  const company = useCompany()

  return (
    <PageContainer
      params={CALENDAR_PARAMS}
      paramsSchema={calendarParamsSchema}
      usePrimary={useCalendarSimulationResource}
      useSecondary={useCustomerResource}
      back={{ to: `/${company}/loans`, label: t('loan.title') }}
      title={({ signedSimulation, customer }) =>
        `${customer.firstName} ${customer.lastName} - ${signedSimulation.simulation.fullLoan.loan.contractNumber}`
      }
      chip={({ signedSimulation }) => {
        const currentLoanStatus = signedSimulation.simulation.getCurrentLoanStatus()
        return currentLoanStatus === null
          ? { label: t('common.none') }
          : { set: ELoanStatusSet, value: toLoanStatusValue(currentLoanStatus) }
      }}
      subtitle={({ signedSimulation }) =>
        isProd
          ? []
          : [
              {
                label: t('loan.payment.originalLoanId'),
                value: signedSimulation.simulation.fullLoan.loan.originalLoanId ?? t('common.none'),
              },
            ]
      }
      providers={({ signedSimulation }, page) => (
        <LoanLockProvider
          loanId={signedSimulation.simulation.fullLoan.loan.id}
          financingProgram={signedSimulation.simulation.fullLoan.loan.financingProgramId}
          financingCompany={signedSimulation.simulation.fullLoan.loan.financingCompanyId}
        >
          {/* Above the tabs: the pending calendar actions survive a visit to another tab. */}
          <LoanCalendarSimulationProvider originalSignedSimulation={signedSimulation}>
            {page}
          </LoanCalendarSimulationProvider>
        </LoanLockProvider>
      )}
      actions={
        tab === TRANSACTION_TAB
          ? undefined
          : ({ signedSimulation }) => <LoanCalendarActions simulation={signedSimulation.simulation} />
      }
    >
      {({ signedSimulation }) => (
        <PageTabs
          label={t('loan.details.tabs.label')}
          tabs={[
            {
              value: CALENDAR_TAB,
              label: t('loan.details.tabs.calendar'),
              content: <LoanCalendarContent />,
            },
            {
              value: TRANSACTION_TAB,
              label: t('loan.details.tabs.transaction'),
              content: (
                <LoanTransactionList
                  loanId={signedSimulation.simulation.fullLoan.loan.id}
                  financingCompanyId={signedSimulation.simulation.fullLoan.loan.financingCompanyId}
                />
              ),
            },
          ]}
        />
      )}
    </PageContainer>
  )
}

export default LoanCalendarPage
